import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import styles from 'pages/dashboards/v5/styles';
import { Box, HStack, VStack, Heading, Text } from '@chakra-ui/react';
import { EditIcon, AddIcon, ViewIcon } from '@chakra-ui/icons';
import { Card, CardBody } from 'components/v4/Card';
import DashboardServiceCardSbcStatusbar from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardSbcStatusbar';
import ServiceStatus from 'pages/dashboards/v5/old/components/ServiceStatus';
import getSipTrunkCardStatus from 'pages/dashboards/v5/components/helpers/sipTrunkStatus';
import CardDetails from 'pages/dashboards/v5/components/CardDetails';
import DashboardCardEditButton from 'pages/dashboards/v5/components/DashboardCardEditButton';

import trunkStatusCodeToText from 'utils/trunkStatusCodeToText';
import Image from 'components/v4/Image';
import { MenuItem } from 'components/v4';

import useGetSRTTrunks from 'pages/services/new/ServiceDashboard/Services/SRT/queries/useGetSRTTrunks';
import useSRTTrunkTypes from 'hooks/useSRTTrunkTypes';

import { useDashboardMenu } from 'context/DashboardMenuContext';

export default function SRTCard({ service }) {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { darkMode } = useSelector((state) => state.settings);
    const { isQueryDisabled } = useDashboardMenu();
    const currentCompany = useSelector(
        (state) => state.navigationLists.currentCompany,
    );
    const accountScope = useSelector((state) => state.login.requiredScope);

    // SRT trunks are matched by trunk type id — the display name is
    // backend-owned and must not be relied on for filtering.
    const { isSRTTrunk } = useSRTTrunkTypes();

    const { data: trunkData } = useGetSRTTrunks({
        companyID: currentCompany,
        enabled: !isQueryDisabled,
    });

    const [cardStatus, setCardStatus] = useState({
        trunkCount: '...',
        channelCount: '...',
        status: '...',
        statusCode: null,
    });

    const calculateSrtStatus = useCallback(
        (data) => {
            const srtTrunks = (data?.data || []).filter(isSRTTrunk);

            let trunkCount = 0;
            let channelCount = 0;
            let statusCode = null;
            let status = '';

            srtTrunks.forEach((trunk) => {
                trunkCount++;
                channelCount += trunk.channels;
            });

            const isError = srtTrunks
                .map((t) => (t.status === 20 ? t.status : undefined))
                .filter((n) => n !== undefined);
            const isWarning = srtTrunks
                .map((t) =>
                    t.status === 8 || t.status === 9 ? t.status : undefined,
                )
                .filter((n) => n !== undefined);
            const isOk = srtTrunks
                .map((t) =>
                    t.status >= 0 && t.status <= 4 ? t.status : undefined,
                )
                .filter((n) => n !== undefined);

            if (isError.length) {
                statusCode = Math.max(...isError);
                status = trunkStatusCodeToText(statusCode);
            } else if (isWarning.length) {
                statusCode = Math.max(...isWarning);
                status = trunkStatusCodeToText(statusCode);
            } else if (isOk.length) {
                statusCode = Math.max(...isOk);
                status = trunkStatusCodeToText(statusCode);
            }

            setCardStatus({ trunkCount, channelCount, status, statusCode });
        },
        [isSRTTrunk],
    );

    useEffect(() => {
        if (trunkData) {
            setTimeout(() => {
                calculateSrtStatus(trunkData);
            }, 500);
        }
    }, [trunkData, calculateSrtStatus]);

    const openResourcesMenu = () => {
        const tel = document.querySelector('.Resources');
        if (tel?.children.length === 1) {
            tel.querySelector('div')?.dispatchEvent(
                new MouseEvent('click', {
                    bubbles: true,
                    cancelable: true,
                    view: window,
                }),
            );
        }
    };

    const addTrunks = () => {
        openResourcesMenu();
        dispatch({
            type: 'REDIRECT',
            payload: { pathname: '/resources/trunks', scope: 20 },
        });
        navigate('/trunks/add/srt');
    };

    const viewTrunks = () => {
        openResourcesMenu();
        dispatch({
            type: 'REDIRECT',
            payload: { pathname: '/resources/trunks', scope: 20 },
        });
        navigate('/trunks');
    };

    // (3134) Degraded and Error name the trunk status rather than leaving
    // the pill blank; the collapsed header's badge reads the same.
    const pillStatus = getSipTrunkCardStatus(
        cardStatus.statusCode ?? service?.statusDisplay?.statusDisplayLevel,
    );

    return (
        <Card
            bg={darkMode ? 'dark.tcap' : null}
            sx={{
                minWidth: '250px',
                border: '1px solid',
                borderColor: darkMode ? 'gray.700' : 'gray.200',
                boxShadow: darkMode
                    ? 'none'
                    : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            }}>
            <DashboardServiceCardSbcStatusbar
                sx={styles.verticalBar}
                status={
                    cardStatus.statusCode ??
                    service?.statusDisplay?.statusDisplayLevel
                }
            />
            <CardBody
                sx={{
                    display: 'flex',
                    flexDir: 'column',
                }}>
                <VStack
                    alignItems="flex-start"
                    width="100%"
                    spacing={2}
                    marginBottom="15px">
                    <HStack
                        alignItems="flex-start"
                        width="100%"
                        overflow="hidden">
                        <Image
                            src={CardDetails(service?.serviceType)?.icon}
                            sx={{ maxWidth: '50px', flexShrink: 0 }}
                        />
                        <VStack alignItems="flex-start" width="100%" minW={0}>
                            <Heading as="h4" size="md">
                                {CardDetails(service?.serviceType)?.serviceName}
                            </Heading>
                            {/* Status */}
                            <Box
                                className="flex justify-between"
                                sx={{ paddingBottom: '20px', width: '100%' }}>
                                <ServiceStatus
                                    service={service}
                                    cardStatus={{
                                        ...cardStatus,
                                        ...pillStatus,
                                    }}
                                />
                            </Box>
                            {/* Trunks */}
                            <Box
                                className="mb-[15px] flex justify-between"
                                style={{ width: '60%' }}>
                                <Text className="font-medium">Trunks:</Text>
                                <Text margin={0}>{cardStatus.trunkCount}</Text>
                            </Box>
                            {/* Channels */}
                            <Box
                                className="mb-[15px] flex justify-between"
                                style={{ width: '60%' }}>
                                <Text className="font-medium">Channels:</Text>
                                <Text margin={0}>
                                    {cardStatus.channelCount}
                                </Text>
                            </Box>
                        </VStack>
                    </HStack>
                </VStack>
                <DashboardCardEditButton>
                    <MenuItem
                        icon={<AddIcon />}
                        onClick={addTrunks}
                        data-walkthroughid={`/dashboard-service/card/${service.name}/add-trunk`}>
                        <Text marginBottom="0">Add Trunk</Text>
                    </MenuItem>
                    <MenuItem icon={<ViewIcon />} onClick={viewTrunks}>
                        <Text marginBottom="0">View Trunks</Text>
                    </MenuItem>
                    {accountScope >= 40 && (
                        <Link
                            className="bg-transparent !no-underline hover:text-inherit"
                            to={`/services/srt/${service.id}/edit`}
                            state={{ serviceId: service.id, data: service }}>
                            <MenuItem
                                icon={<EditIcon />}
                                data-walkthroughid={`/dashboard-service/card/${service.name}/edit-service`}>
                                <Text marginBottom="0">Edit Service</Text>
                            </MenuItem>
                        </Link>
                    )}
                </DashboardCardEditButton>
            </CardBody>
        </Card>
    );
}
